import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Building2, Users, MapPin, TrendingUp, ShieldCheck, Pause, Play, Search, Scissors, LogOut } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PlatformAdminPage() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [authorized, setAuthorized] = useState(null);

  const load = useCallback(async () => {
    try {
      const me = await api.get("/auth/me");
      if (me.data?.role !== "platform_admin") { setAuthorized(false); return; }
      setAuthorized(true);
      const res = await api.get("/platform/tenants");
      setTenants(res.data || []);
    } catch {
      setAuthorized(false);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (tenant_id, action) => {
    try {
      await api.post(`/platform/tenants/${tenant_id}/status`, { action });
      toast.success(action === "suspend" ? "Workspace suspended" : "Workspace reactivated");
      load();
    } catch { toast.error("Failed"); }
  };

  const logout = () => {
    localStorage.removeItem("salonflow_token");
    navigate("/platform/login");
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-950"><div className="w-8 h-8 border-2 border-rose-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!authorized) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <div className="text-center">
          <ShieldCheck className="w-12 h-12 text-rose-400 mx-auto mb-4" />
          <p className="text-white font-['Outfit'] text-xl mb-2">Platform access required</p>
          <Button onClick={() => navigate("/platform/login")} className="mt-3 rounded-full h-10 px-5 bg-white text-slate-900">
            Platform sign in
          </Button>
        </div>
      </div>
    );
  }

  const filtered = q ? tenants.filter(t =>
    t.business_name?.toLowerCase().includes(q.toLowerCase()) || t.slug?.includes(q.toLowerCase())
  ) : tenants;

  const total = tenants.length;
  const active = tenants.filter(t => t.is_active).length;
  const totalUsers = tenants.reduce((s, t) => s + (t.user_count || 0), 0);
  const totalClients = tenants.reduce((s, t) => s + (t.client_count || 0), 0);

  return (
    <div className="min-h-screen bg-slate-950" data-testid="platform-admin-page">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-0 -left-40 w-96 h-96 rounded-full bg-rose-500/5 blur-3xl" />
        <div className="absolute bottom-0 -right-40 w-96 h-96 rounded-full bg-violet-500/5 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-rose-500 via-violet-500 to-teal-500 flex items-center justify-center">
              <Scissors className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-['Outfit'] text-2xl font-semibold tracking-tight text-white">Platform Control</h1>
              <p className="text-white/40 text-xs font-['Manrope'] uppercase tracking-widest">SalonFlow SaaS Admin</p>
            </div>
          </div>
          <Button variant="outline" onClick={logout} className="border-white/10 bg-white/5 text-white hover:bg-white/10 rounded-full h-9 px-4 text-xs">
            <LogOut className="w-3 h-3 mr-1.5" />Sign out
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Workspaces", value: total, icon: Building2 },
            { label: "Active", value: active, icon: ShieldCheck },
            { label: "Users on Platform", value: totalUsers, icon: Users },
            { label: "Clients on Platform", value: totalClients, icon: TrendingUp },
          ].map(s => (
            <div key={s.label} className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <s.icon className="w-3.5 h-3.5 text-white/40" />
                <span className="text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">{s.label}</span>
              </div>
              <p className="font-['Outfit'] text-3xl font-semibold text-white tabular-nums">{s.value}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4 max-w-md">
          <Search className="w-4 h-4 text-white/30 absolute left-4 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or slug…"
            data-testid="platform-search"
            className="w-full h-11 pl-11 pr-4 bg-white/5 border border-white/10 text-white placeholder:text-white/30 rounded-full text-sm focus:outline-none focus:border-white/20 font-['Manrope']" />
        </div>

        {/* Tenant table */}
        <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Business</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Slug</th>
                <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Plan</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Users</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Locations</th>
                <th className="text-right px-4 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Clients</th>
                <th className="text-right px-5 py-3 text-[10px] font-bold uppercase tracking-widest text-white/40 font-['Manrope']">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.tenant_id} className="border-b border-white/5 hover:bg-white/5 transition-colors" data-testid={`tenant-row-${t.slug}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-semibold text-xs font-['Outfit']"
                        style={{ backgroundColor: t.branding?.primary_color || "#64748B" }}>
                        {t.business_name?.[0]}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white font-['Outfit']">{t.business_name}</p>
                        <p className="text-[11px] text-white/30 font-['Manrope']">{t.business_type}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3.5"><code className="text-xs text-white/60 font-mono">{t.slug}</code></td>
                  <td className="px-4 py-3.5">
                    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      t.plan_status === "active" ? "bg-emerald-500/20 text-emerald-300" :
                      t.plan_status === "trialing" ? "bg-amber-500/20 text-amber-300" :
                      t.plan_status === "suspended" ? "bg-rose-500/20 text-rose-300" : "bg-white/10 text-white/50"
                    }`}>
                      {t.plan_status || t.plan}
                    </span>
                  </td>
                  <td className="px-4 py-3.5 text-sm text-right text-white tabular-nums">{t.user_count}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-white tabular-nums">{t.location_count}</td>
                  <td className="px-4 py-3.5 text-sm text-right text-white tabular-nums">{t.client_count}</td>
                  <td className="px-5 py-3.5 text-right">
                    <div className="flex items-center gap-1.5 justify-end">
                      <a href={`/t/${t.slug}/login`} target="_blank" rel="noreferrer"
                        className="text-[11px] text-white/60 hover:text-white px-2 py-1 rounded bg-white/5 hover:bg-white/10 font-['Manrope']">
                        Open
                      </a>
                      {t.is_active ? (
                        <button onClick={() => setStatus(t.tenant_id, "suspend")}
                          data-testid={`suspend-${t.slug}`}
                          className="text-[11px] text-rose-300 hover:text-rose-200 px-2 py-1 rounded bg-rose-500/10 hover:bg-rose-500/20 font-['Manrope'] flex items-center gap-1">
                          <Pause className="w-3 h-3" />Suspend
                        </button>
                      ) : (
                        <button onClick={() => setStatus(t.tenant_id, "reactivate")}
                          data-testid={`reactivate-${t.slug}`}
                          className="text-[11px] text-emerald-300 hover:text-emerald-200 px-2 py-1 rounded bg-emerald-500/10 hover:bg-emerald-500/20 font-['Manrope'] flex items-center gap-1">
                          <Play className="w-3 h-3" />Resume
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-12 text-center text-white/30 text-sm font-['Manrope']">No workspaces match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-center text-white/30 text-xs mt-6 font-['Manrope']">
          <button onClick={async () => {
            try { await axios.post(`${API}/platform/seed-demo-tenants`); toast.success("Demo tenants seeded"); load(); }
            catch { toast.error("Failed"); }
          }} className="hover:text-white/60 underline">Seed demo tenants</button>
        </p>
      </div>
    </div>
  );
}
